<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="turnaround-server-table"
      class="w-100"
    >
      <div v-if="!sample_type_id" slot="beforeTable" class="pt-3">
        <b-row>
          <b-col>
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('sample_type', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="sample_type.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['sample_type']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/sampletypes/${getData(row, {
                levels: ['sample_type'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["sample_type"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="name" slot-scope="{ row }">
        <a :href="`#/turnarounds/${row.id}`" class="btn-link">
          {{ row.name }}
        </a>
      </span>
      <span slot="surcharge" slot-scope="{ row }">
        {{ formatSurcharge(row) }}
      </span>
      <span slot="turnaround" slot-scope="{ row }">
        {{ formatTurnaround(row) }}
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit turnaround')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/turnarounds/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete turnaround')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import SelectFilter from "@/components/shared/SelectFilter";
import TurnaroundCollection from "@/collections/lab/TurnaroundCollection";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getEntityData } from "@/shared/utils";
import { getCollection } from "@/shared/fetch-utils";

Vue.use(ServerTable);

export default {
  name: "TurnaroundServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ModalTrashBasket,
    ServerTable,
    SelectFilter,
  },
  props: {
    collection: {
      type: Object,
      default: () => new TurnaroundCollection(),
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    laboratory_id: {
      type: String,
      default: null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "sample_type.name",
        "name",
        "turnaround",
        "surcharge",
        "description",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => ["sample_type.name", "name", "turnaround", "surcharge"],
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit turnaround") ||
      this.checkPermission("delete turnaround");

    return {
      entity_type: "turnaround",
      sample_type_collection: null,
      sample_type_id: this.$route.params.sample_type_id || null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            "sample_type.name": "Sample Type",
          },
          filterable: this.filterable,
          sortable: this.sortable,
        },
      }),
    };
  },
  computed: {
    ...mapGetters({
      trash: "turnaroundTrash",
    }),
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    show_modal() {
      return (
        this.checkPermission("delete turnaround") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async init() {
      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          collection_type: "sample_type",
          sort: [
            {
              logic: "sortAddAsc",
              field: "name",
            },
          ],
          message: this.$bvToast,
        }));
    },
    formatSurcharge(row) {
      if (row.surcharge_type === "flat") {
        return `$${row.surcharge.toFixed(2)}`;
      } else if (row.surcharge_type === "percent") {
        return `${parseFloat(row.surcharge)}%`;
      }
    },
    formatTurnaround(row) {
      return `${row.turnaround} Day${row.turnaround === 1 ? "" : "s"}`;
    },
    prepareCollectionFilter(filter) {
      this.sample_type_id &&
        filter.filterEquals("sample_type_id", this.sample_type_id);
      this.laboratory_id &&
        filter.filterEquals("laboratory_id", this.laboratory_id);

      return filter;
    },
  },
  created() {
    this.init();
  },
};
</script>
